import { useState, useMemo } from "react";
import { useStudio } from "../studios/StudioContext";
import { StripeCardForm } from "./StripeCardForm";
import { formatTime, parseLocalDate, resolveImg, KIND_LABELS, workshopKind } from "./calendarUtils";

const FULL_LABEL = { workshop: "Full Workshop", series: "Full Series", course: "Full Course" };

export function WorkshopBookingModal({ workshop, onClose }) {
  const studio  = useStudio();
  const API     = studio.apiBaseUrl;
  const venues  = studio?.venues ?? [];
  const kind    = workshopKind(workshop);
  const single  = kind === "workshop" || workshop.allowSingle !== false;   // single sessions on sale?
  const total   = workshop.days.reduce((n, d) => n + d.sessions.length, 0);
  const image   = resolveImg(API, workshop.image);

  const [selected, setSelected]   = useState(() => (single ? new Set() : new Set(["full"])));
  const [name, setName]           = useState("");
  const [email, setEmail]         = useState("");
  const [booked, setBooked]       = useState(false);
  const [bookError, setBookError] = useState("");

  const totalPrice = useMemo(() => {
    if (selected.has("full")) return workshop.pricing.fullWorkshop;
    let sum = 0;
    workshop.days.forEach((day, di) => day.sessions.forEach(sess => {
      if (selected.has(`${di}__${sess.id}`)) sum += Number(sess.price) || 0;
    }));
    return sum;
  }, [selected, workshop]);

  const toggle = key => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else { next.delete("full"); next.add(key); }
    return next;
  });
  const toggleFull = () => { if (single) setSelected(prev => (prev.has("full") ? new Set() : new Set(["full"]))); };

  const handlePaymentSuccess = async paymentIntentId => {
    const sessions = [];
    workshop.days.forEach((day, di) => day.sessions.forEach(sess => {
      if (selected.has("full") || selected.has(`${di}__${sess.id}`)) {
        sessions.push({ id: `${di}__${sess.id}`, date: day.date, label: sess.label, time: sess.time });
      }
    }));
    try {
      const bookRes = await fetch(`${API}/bookings/workshop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name, email,
          workshopId: workshop.id, workshopTitle: workshop.title,
          sessions, totalPrice,
          paymentType: "dropin", stripePaymentId: paymentIntentId,
        }),
      });
      const bookData = await bookRes.json();
      if (!bookRes.ok) throw new Error(bookData.error ?? "Booking failed.");
      setBooked(true);
    } catch (err) {
      setBookError(err.message);
    }
  };

  if (booked) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-card workshop-booking-modal" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>✕</button>
          <div className="booking-success">
            <div className="booking-success-icon">✓</div>
            <h2 className="booking-success-title">You're booked!</h2>
            <p className="booking-success-sub">Check your email for confirmation details.</p>
            <button className="book-btn" style={{marginTop:"1.25rem"}} onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card workshop-booking-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        {image && <img className="ws-modal-image" src={image} alt="" />}
        <div className="modal-badge badge-workshop">{KIND_LABELS[kind]}</div>
        <h2 className="modal-title">{workshop.title}</h2>
        {workshop.teacher && <p className="workshop-teacher">with {workshop.teacher}</p>}
        {workshop.description && <p className="workshop-desc">{workshop.description}</p>}

        <div className={`booking-option full-workshop-option ${selected.has("full") ? "selected" : ""}`} onClick={toggleFull}>
          <div className="booking-option-left">
            <span className="booking-option-label">{FULL_LABEL[kind]}</span>
            <span className="booking-option-sub">All {total} session{total !== 1 ? "s" : ""}</span>
          </div>
          <span className="booking-option-price">${workshop.pricing.fullWorkshop}</span>
          <span className="booking-option-check">{selected.has("full") ? "✓" : ""}</span>
        </div>

        {single && <div className="booking-divider">— or choose individual sessions —</div>}

        {workshop.days.map((day, di) => {
          const dayLabel = parseLocalDate(day.date).toLocaleDateString("en-US", {weekday:"short", month:"short", day:"numeric"});
          const venue    = venues.find(v => v.id === day.venue);
          return (
            <div key={di} className="booking-day-block">
              <div className="booking-day-header">
                <div>
                  <span className="booking-day-label">{dayLabel}</span>
                  <span className="booking-day-venue">{venue?.name ?? day.venue}</span>
                </div>
              </div>
              {day.sessions.map(sess => {
                const key   = `${di}__${sess.id}`;
                const isSel = selected.has("full") || selected.has(key);
                const pick  = single && !selected.has("full");
                return (
                  <div key={sess.id}
                    className={`booking-option session-option ${isSel ? "selected" : ""} ${pick ? "" : "is-static"}`}
                    onClick={() => { if (pick) toggle(key); }}>
                    <div className="booking-option-left">
                      <span className="booking-option-label">{sess.label}</span>
                      <span className="booking-option-sub">{formatTime(sess.time)} · {sess.duration} min</span>
                    </div>
                    {single && <span className="booking-option-price">${sess.price}</span>}
                    <span className="booking-option-check">{isSel ? "✓" : ""}</span>
                  </div>
                );
              })}
            </div>
          );
        })}

        <div className="booking-fields" style={{marginTop:"1.25rem"}}>
          <label className="form-label">Full Name
            <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Jane Smith" />
          </label>
          <label className="form-label">Email
            <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" />
          </label>
        </div>

        <div className="booking-total" style={{margin:"0.75rem 0"}}>Total: <strong>${totalPrice}</strong></div>

        <StripeCardForm
          amount={Math.round(totalPrice * 100)}
          email={email}
          billingDetails={{ email }}
          onSuccess={handlePaymentSuccess}
          onError={msg => setBookError(msg)}
          submitLabel={selected.has("full") ? `Book ${FULL_LABEL[kind]}` : "Book Selected Sessions"}
          disabled={selected.size === 0 || !name.trim() || !email.trim()}
        />
        {bookError && <p className="pass-feedback invalid" style={{marginTop:"0.75rem"}}>{bookError}</p>}
      </div>
    </div>
  );
}
